import { useId, type ComponentProps, type ReactNode } from 'react';
import { Input } from 'ui/components/input';
import { Label } from 'ui/components/label';
import { cn } from 'ui/lib/utils';

interface TextFieldProps extends Omit<ComponentProps<'input'>, 'onChange' | 'size'> {
	label?: string;
	hint?: string;
	error?: string;
	/** Recibe el valor ya como texto; con `maxLength` en un input numérico recorta el exceso. */
	onValueChange?: (value: string) => void;
	startAdornment?: ReactNode;
	endAdornment?: ReactNode;
}

/**
 * Extensión local: etiqueta + `Input` + ayuda/error para formularios controlados a mano (los que usaban
 * `FormGenericInput`). Los formularios con react-hook-form usan `FormField` de `ui/components/form`.
 * `maxLength` también rige en `type="number"`, donde el atributo nativo no se aplica.
 */
export function TextField({
	label,
	hint,
	error,
	onValueChange,
	maxLength,
	startAdornment,
	endAdornment,
	id,
	className,
	required,
	...props
}: TextFieldProps) {
	const autoId = useId();
	const fieldId = id ?? autoId;
	const describedBy = error ? `${fieldId}-error` : hint ? `${fieldId}-hint` : undefined;

	return (
		<div className="grid gap-1.5">
			{label && (
				<Label htmlFor={fieldId}>
					{label}
					{required && <span aria-hidden="true"> *</span>}
				</Label>
			)}
			<div className="relative flex items-center">
				{startAdornment && <span className="pointer-events-none absolute left-3">{startAdornment}</span>}
				<Input
					id={fieldId}
					required={required}
					aria-invalid={error ? true : undefined}
					aria-describedby={describedBy}
					className={cn(error && 'border-destructive', startAdornment && 'pl-9', endAdornment && 'pr-9', className)}
					onChange={(e) => {
						const raw = e.target.value;
						onValueChange?.(maxLength && raw.length > maxLength ? raw.slice(0, maxLength) : raw);
					}}
					{...props}
				/>
				{endAdornment && <span className="absolute right-3">{endAdornment}</span>}
			</div>
			{hint && !error && (
				<p
					id={`${fieldId}-hint`}
					className="text-sm text-muted-foreground">
					{hint}
				</p>
			)}
			{error && error.trim() && (
				<p
					id={`${fieldId}-error`}
					role="alert"
					className="text-sm text-destructive">
					{error}
				</p>
			)}
		</div>
	);
}
